<template>
  <b-sidebar
    id="add-member-sidebar"
    :visible="isOpen"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => !val && $emit('close')"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ $t('addMember') }}
        </h5>
        <feather-icon
          class="cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>
      
      <b-form @submit.prevent="handleSubmit" class="p-2">
        <!-- Email (Required) -->
        <b-form-group
          :label="$t('email')"
          label-for="member-email"
        >
          <b-form-input
            id="member-email"
            v-model="form.member_email"
            type="email"
            :placeholder="$t('enterEmail')"
            :state="emailState"
            required
          />
          <b-form-invalid-feedback :state="emailState">
            {{ $t('emailRequired') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <!-- First Name (Required) -->
        <b-form-group
          :label="$t('firstName')"
          label-for="member-first-name"
        >
          <b-form-input
            id="member-first-name"
            v-model="form.first_name"
            :placeholder="$t('enterFirstName')"
            required
          />
        </b-form-group>

        <!-- Last Name (Required) -->
        <b-form-group
          :label="$t('lastName')"
          label-for="member-last-name"
        >
          <b-form-input
            id="member-last-name"
            v-model="form.last_name"
            :placeholder="$t('enterLastName')"
            required
          />
        </b-form-group>

        <!-- Role (Required) -->
        <b-form-group
          :label="$t('role')"
          label-for="member-role"
        >
          <v-select
            id="member-role"
            v-model="selectedRole"
            :options="roleOptions"
            :reduce="option => option.value"
            label="text"
            :clearable="false"
          />
        </b-form-group>

        <!-- Buttons -->
        <div class="d-flex mt-2">
          <b-button
            variant="primary"
            class="mr-2"
            type="submit"
            :disabled="loading"
          >
            <b-spinner v-if="loading" small class="mr-50" />
            {{ $t('save') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            @click="hide"
          >
            {{ $t('cancel') }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import Ripple from 'vue-ripple-directive';
import { addWhitelabelMember } from '@/services/settings';
import { showToast } from '@/libs/utils/others';

export default {
  name: 'AddMemberSidebar',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      form: {
        member_email: '',
        first_name: '',
        last_name: '',
        role_member: 'member',
      },
      loading: false,
      roleOptions: [
        { value: 'member', text: this.$t('member') },
        { value: 'admin', text: this.$t('admin') },
      ],
    };
  },
  computed: {
    emailState() {
      if (!this.form.member_email) return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.form.member_email);
    },
    selectedRole: {
      get() {
        return this.form.role_member;
      },
      set(value) {
        this.form.role_member = value;
      }
    }
  },
  methods: {
    handleSubmit() {
      if (!this.emailState) {
        showToast(this, 'danger', 'XIcon', 'errorTitle', 'emailRequired');
        return;
      }

      if (!this.form.first_name || !this.form.last_name || !this.form.role_member) {
        showToast(this, 'danger', 'XIcon', 'errorTitle', 'allFieldsRequired');
        return;
      }

      this.loading = true;

      const payload = {
        member_email: this.form.member_email,
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        role_member: this.form.role_member,
      };

      addWhitelabelMember(payload)
        .then(response => {
          showToast(this, 'success', 'CheckIcon', 'success', 'memberAddedSuccessfully');
          this.$emit('member-added', response);
          this.resetForm();
        })
        .catch(() => {
          showToast(this, 'danger', 'XIcon', 'errorTitle', 'errorAddingMember');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetForm() {
      this.form = {
        member_email: '',
        first_name: '',
        last_name: '',
        role_member: 'member',
      };
    },
  },
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.content-sidebar-header {
  border-bottom: 1px solid #ebe9f1;
}

::v-deep .sidebar-lg {
  width: 500px;
}
</style>
